import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';

export const LayoutPatternsSection = () => {
  return (
    <div id='layout-patterns' className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold mb-2'>Layout Patterns</h2>
        <p className='text-gray-600 mb-4'>
          Common layout patterns and utility classes used throughout the
          application.
        </p>
      </div>

      {/* Grid Layouts */}
      <Card title='Grid Layouts' className='mb-4'>
        <div className='space-y-6'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Two-Column Form
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label
                  htmlFor='form-firstname'
                  className='block text-sm font-medium mb-2'
                >
                  First Name
                </label>
                <InputText
                  id='form-firstname'
                  placeholder='John'
                  className='w-full'
                />
              </div>
              <div>
                <label
                  htmlFor='form-lastname'
                  className='block text-sm font-medium mb-2'
                >
                  Last Name
                </label>
                <InputText
                  id='form-lastname'
                  placeholder='Doe'
                  className='w-full'
                />
              </div>
              <div>
                <label
                  htmlFor='form-email'
                  className='block text-sm font-medium mb-2'
                >
                  Email
                </label>
                <InputText
                  id='form-email'
                  placeholder='john@example.com'
                  className='w-full'
                />
              </div>
              <div>
                <label
                  htmlFor='form-phone'
                  className='block text-sm font-medium mb-2'
                >
                  Phone
                </label>
                <InputText
                  id='form-phone'
                  placeholder='(555) 123-4567'
                  className='w-full'
                />
              </div>
            </div>
          </div>

          <Divider />

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Three-Column Grid
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <Card className='text-center'>
                <i className='pi pi-users text-4xl text-blue-500 mb-3'></i>
                <h4 className='font-semibold mb-1'>Users</h4>
                <p className='text-2xl font-bold'>1,234</p>
              </Card>
              <Card className='text-center'>
                <i className='pi pi-chart-bar text-4xl text-green-500 mb-3'></i>
                <h4 className='font-semibold mb-1'>Responses</h4>
                <p className='text-2xl font-bold'>5,678</p>
              </Card>
              <Card className='text-center'>
                <i className='pi pi-clock text-4xl text-purple-500 mb-3'></i>
                <h4 className='font-semibold mb-1'>Avg. Time</h4>
                <p className='text-2xl font-bold'>3m 42s</p>
              </Card>
            </div>
          </div>

          <Divider />

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Four-Column Grid
            </h3>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              <div className='bg-blue-50 p-4 rounded-lg text-center'>
                <p className='text-sm text-gray-600 mb-1'>Total</p>
                <p className='text-2xl font-bold text-blue-700'>456</p>
              </div>
              <div className='bg-green-50 p-4 rounded-lg text-center'>
                <p className='text-sm text-gray-600 mb-1'>Active</p>
                <p className='text-2xl font-bold text-green-700'>123</p>
              </div>
              <div className='bg-yellow-50 p-4 rounded-lg text-center'>
                <p className='text-sm text-gray-600 mb-1'>Pending</p>
                <p className='text-2xl font-bold text-yellow-700'>89</p>
              </div>
              <div className='bg-red-50 p-4 rounded-lg text-center'>
                <p className='text-sm text-gray-600 mb-1'>Closed</p>
                <p className='text-2xl font-bold text-red-700'>244</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Flexbox Utilities */}
      <Card title='Flexbox Utilities' className='mb-4'>
        <div className='space-y-6'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Horizontal Alignment
            </h3>
            <div className='space-y-3'>
              <div className='flex justify-start gap-2 bg-gray-50 p-3 rounded'>
                <div className='bg-blue-500 text-white px-4 py-2 rounded'>
                  Start
                </div>
                <div className='bg-blue-500 text-white px-4 py-2 rounded'>
                  Items
                </div>
              </div>
              <div className='flex justify-center gap-2 bg-gray-50 p-3 rounded'>
                <div className='bg-green-500 text-white px-4 py-2 rounded'>
                  Center
                </div>
                <div className='bg-green-500 text-white px-4 py-2 rounded'>
                  Items
                </div>
              </div>
              <div className='flex justify-end gap-2 bg-gray-50 p-3 rounded'>
                <div className='bg-purple-500 text-white px-4 py-2 rounded'>
                  End
                </div>
                <div className='bg-purple-500 text-white px-4 py-2 rounded'>
                  Items
                </div>
              </div>
              <div className='flex justify-between gap-2 bg-gray-50 p-3 rounded'>
                <div className='bg-orange-500 text-white px-4 py-2 rounded'>
                  Between
                </div>
                <div className='bg-orange-500 text-white px-4 py-2 rounded'>
                  Items
                </div>
              </div>
            </div>
          </div>

          <Divider />

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Vertical Alignment
            </h3>
            <div className='space-y-3'>
              <div className='flex items-start gap-2 bg-gray-50 p-3 rounded h-24'>
                <div className='bg-blue-500 text-white px-4 py-2 rounded'>
                  Top
                </div>
                <div className='bg-blue-500 text-white px-4 py-2 rounded'>
                  Aligned
                </div>
              </div>
              <div className='flex items-center gap-2 bg-gray-50 p-3 rounded h-24'>
                <div className='bg-green-500 text-white px-4 py-2 rounded'>
                  Center
                </div>
                <div className='bg-green-500 text-white px-4 py-2 rounded'>
                  Aligned
                </div>
              </div>
              <div className='flex items-end gap-2 bg-gray-50 p-3 rounded h-24'>
                <div className='bg-purple-500 text-white px-4 py-2 rounded'>
                  Bottom
                </div>
                <div className='bg-purple-500 text-white px-4 py-2 rounded'>
                  Aligned
                </div>
              </div>
            </div>
          </div>

          <Divider />

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Gap Spacing
            </h3>
            <div className='space-y-3'>
              <div className='flex gap-2 bg-gray-50 p-3 rounded'>
                <div className='bg-blue-500 text-white px-4 py-2 rounded'>
                  Gap
                </div>
                <div className='bg-blue-500 text-white px-4 py-2 rounded'>
                  2
                </div>
              </div>
              <div className='flex gap-4 bg-gray-50 p-3 rounded'>
                <div className='bg-green-500 text-white px-4 py-2 rounded'>
                  Gap
                </div>
                <div className='bg-green-500 text-white px-4 py-2 rounded'>
                  4
                </div>
              </div>
              <div className='flex gap-6 bg-gray-50 p-3 rounded'>
                <div className='bg-purple-500 text-white px-4 py-2 rounded'>
                  Gap
                </div>
                <div className='bg-purple-500 text-white px-4 py-2 rounded'>
                  6
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Spacing Utilities */}
      <Card title='Spacing Utilities' className='mb-4'>
        <div className='space-y-6'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Padding Examples
            </h3>
            <div className='space-y-3'>
              <div className='bg-gray-50 p-2 rounded'>
                <div className='bg-blue-100 border-2 border-blue-300 p-2'>
                  Padding: p-2
                </div>
              </div>
              <div className='bg-gray-50 p-2 rounded'>
                <div className='bg-green-100 border-2 border-green-300 p-4'>
                  Padding: p-4
                </div>
              </div>
              <div className='bg-gray-50 p-2 rounded'>
                <div className='bg-purple-100 border-2 border-purple-300 p-6'>
                  Padding: p-6
                </div>
              </div>
            </div>
          </div>

          <Divider />

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Margin Examples
            </h3>
            <div className='bg-gray-50 p-4 rounded'>
              <div className='bg-blue-100 border-2 border-blue-300 p-2 mb-2'>
                Margin Bottom: mb-2
              </div>
              <div className='bg-green-100 border-2 border-green-300 p-2 mb-4'>
                Margin Bottom: mb-4
              </div>
              <div className='bg-purple-100 border-2 border-purple-300 p-2'>
                No Margin
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Card-based Sections */}
      <Card title='Card-based Section Pattern' className='mb-4'>
        <p className='text-sm text-gray-600 mb-4'>
          Most pages use card-based layouts to organize content into logical
          sections.
        </p>
        <div className='space-y-4'>
          <Card title='Section 1' className='bg-blue-50'>
            <p className='text-gray-600'>
              Content for the first section goes here.
            </p>
            <div className='flex gap-2 mt-4'>
              <Button label='Action 1' size='small' />
              <Button label='Action 2' size='small' outlined />
            </div>
          </Card>

          <Card title='Section 2' className='bg-green-50'>
            <p className='text-gray-600'>
              Content for the second section goes here.
            </p>
            <div className='flex gap-2 mt-4'>
              <Button label='Save' size='small' severity='success' />
              <Button label='Cancel' size='small' severity='success' outlined />
            </div>
          </Card>

          <Card title='Section 3' className='bg-purple-50'>
            <p className='text-gray-600'>
              Content for the third section goes here.
            </p>
            <div className='flex justify-end gap-2 mt-4'>
              <Button label='Edit' icon='pi pi-pencil' size='small' />
            </div>
          </Card>
        </div>
      </Card>

      {/* Divider */}
      <Card title='Divider Component' className='mb-4'>
        <div className='space-y-4'>
          <p className='text-gray-600'>Content above divider</p>
          <Divider />
          <p className='text-gray-600'>Content below divider</p>
          <Divider align='left'>
            <span className='text-sm text-gray-500'>Left Aligned</span>
          </Divider>
          <p className='text-gray-600'>Content with labeled divider</p>
          <Divider align='center'>
            <span className='text-sm text-gray-500'>Center Label</span>
          </Divider>
          <p className='text-gray-600'>More content</p>
        </div>
      </Card>
    </div>
  );
};
